import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '../App.css';
import {
  FaLanguage,
  FaAngleDown,
  FaSun,
  FaMoon,
  FaSignInAlt,
  FaSignOutAlt,
} from 'react-icons/fa';

// 登出提示模態框
const LogoutModal = ({ isOpen, onClose, title, message }) => {
  if (!isOpen) return null;
  return (
    <div
      className="modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '10px',
          maxWidth: '350px',
          width: '90%',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '15px',
            background: 'none',
            border: 'none',
            fontSize: '22px',
            color: '#999',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
        <h3
          style={{
            marginBottom: '15px',
            color: '#8e7a55',
            fontSize: '20px',
            fontWeight: 600,
          }}
        >
          {title}
        </h3>
        <p
          style={{
            marginBottom: '20px',
            fontSize: '15px',
            lineHeight: '1.5',
            color: '#555',
          }}
        >
          {message}
        </p>
        <button
          onClick={onClose}
          style={{
            padding: '8px 20px',
            backgroundColor: '#8e7a55',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          OK
        </button>
      </div>
    </div>
  );
};

function TopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // 不再使用 localStorage.getItem('token')
  // 改為預設 false，並透過後端 API 檢查實際登入狀態
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // 追蹤語言
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');

  // 追蹤是否顯示登出提示框
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // 按鈕樣式狀態
  const [activeButton, setActiveButton] = useState(null);
  const [prevPath, setPrevPath] = useState('');

  // 手機版 UI
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // 深色模式
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('darkMode') === 'true'
  );

  // 在掛載時依照 localStorage 套用 dark-mode body class
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 每次路徑改變時，向後端檢查是否登入
  useEffect(() => {
    async function checkAuth() {
      try {
        const response = await axios.get('/api/denglu', {
          withCredentials: true,
        });
        setIsLoggedIn(response.data.loggedIn);
      } catch (error) {
        setIsLoggedIn(false);
      }
    }
    checkAuth();
  }, [location.pathname]);

  // 初始化語言與按鈕
  useEffect(() => {
    const savedLang = localStorage.getItem('selectedLanguage');
    if (savedLang) setCurrentLang(savedLang);
    setInitialActiveButton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 切換語言
  useEffect(() => {
    setCurrentLang(i18n.language);
    localStorage.setItem('selectedLanguage', i18n.language);
  }, [i18n.language]);

  // 視窗大小改變時，更新 isMobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    setInitialActiveButton();
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // 判斷當前路由，設定 activeButton
  useEffect(() => {
    if (prevPath === '/login' && location.pathname !== '/login') {
      setActiveButton(null);
      localStorage.removeItem('activeTopBarButton');
    }
    setPrevPath(location.pathname);

    // 根據路由片段更新 activeButton
    if (!(prevPath === '/login' && location.pathname !== '/login')) {
      if (location.pathname === '/login') {
        setActiveButton('login');
        localStorage.setItem('activeTopBarButton', 'login');
      } else if (location.pathname.includes('/investment-basics')) {
        setActiveButton('investment');
        localStorage.setItem('activeTopBarButton', 'investment');
      } else if (location.pathname.includes('/valuation-models')) {
        setActiveButton('valuation');
        localStorage.setItem('activeTopBarButton', 'valuation');
      } else if (location.pathname.includes('/equity-research')) {
        setActiveButton('equity');
        localStorage.setItem('activeTopBarButton', 'equity');
      } else if (location.pathname.includes('/ma-analysis')) {
        setActiveButton('ma');
        localStorage.setItem('activeTopBarButton', 'ma');
      } else if (location.pathname.includes('/tools')) {
        setActiveButton('tools');
        localStorage.setItem('activeTopBarButton', 'tools');
      } else if (location.pathname.includes('/about')) {
        setActiveButton('about');
        localStorage.setItem('activeTopBarButton', 'about');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // 根據 path 設定初始按鈕
  const setInitialActiveButton = () => {
    const path = location.pathname;
    if (path === '/login') {
      setActiveButton('login');
      localStorage.setItem('activeTopBarButton', 'login');
    } else if (path === '/') {
      setActiveButton('home');
      localStorage.setItem('activeTopBarButton', 'home');
    } else {
      setActiveButton(null);
    }
  };

  // 切換語言
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setCurrentLang(lng);
    setShowLanguageDropdown(false);
  };

  // 前往登入頁
  const handleLoginClick = () => {
    navigate('/login');
    setActiveButton('login');
    localStorage.setItem('activeTopBarButton', 'login');
  };

  // 登出邏輯
  const handleLogout = async () => {
    try {
      await axios.get('/api/denglu?logout=true', { withCredentials: true });
      console.log('Logout API done121414124142141412421');
    } catch (error) {
      console.error('Logout error:', error);
    }
    setIsLoggedIn(false);
    setShowLogoutModal(true);
  };

  // 關閉提示框後，導向首頁
  const closeLogoutModal = () => {
    setShowLogoutModal(false);
    navigate('/');
  };

  // 深色模式
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    if (newDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('darkMode', newDarkMode.toString());
  };

  const toggleLanguageDropdown = () => {
    setShowLanguageDropdown(!showLanguageDropdown);
  };

  const isLoginPage = location.pathname === '/login';
  const topBarClass = isLoginPage ? 'TopBar login-page' : 'TopBar';

  return (
    <div className={topBarClass}>
      {/* 左側按鈕 - 語言與主頁 */}
      <div className="left-buttons">
        {!isMobile && (
          <>
            <button
              onClick={() => changeLanguage('en')}
              className={`lang-btn ${currentLang === 'en' ? 'lang-btn-active' : ''}`}
            >
              English
            </button>
            <button
              onClick={() => changeLanguage('zh')}
              className={`lang-btn ${currentLang === 'zh' ? 'lang-btn-active' : ''}`}
            >
              中文
            </button>
            <button
              onClick={() => changeLanguage('ja')}
              className={`lang-btn ${currentLang === 'ja' ? 'lang-btn-active' : ''}`}
            >
              日本語
            </button>
          </>
        )}

        <button
          className={`darkmode-btn ${darkMode ? 'btn-active' : ''}`}
          onClick={toggleDarkMode}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          title={darkMode ? 'Light mode' : 'Dark mode'}
        >
          {darkMode ? (
            <FaSun className="topbar-icon" />
          ) : (
            <FaMoon className="topbar-icon" />
          )}
        </button>

        {isMobile && (
          <div className="dropdown-container">
            <button
              className="dropdown-button language-dropdown-button"
              onClick={toggleLanguageDropdown}
            >
              <FaLanguage /> <FaAngleDown />
            </button>
            {showLanguageDropdown && (
              <div className="dropdown-menu">
                <button
                  className={`lang-btn ${i18n.language === 'en' ? 'lang-btn-active' : ''}`}
                  onClick={() => changeLanguage('en')}
                >
                  English
                </button>
                <button
                  className={`lang-btn ${i18n.language === 'zh' ? 'lang-btn-active' : ''}`}
                  onClick={() => changeLanguage('zh')}
                >
                  中文
                </button>
                <button
                  className={`lang-btn ${i18n.language === 'ja' ? 'lang-btn-active' : ''}`}
                  onClick={() => changeLanguage('ja')}
                >
                  日本語
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 右側按鈕：根據是否已登入顯示登入或登出 */}
      {!isLoginPage && (
        <div className="right-buttons">
          {isLoggedIn ? (
            <button className="logout-button" onClick={handleLogout}>
              {isMobile ? (
                <FaSignOutAlt className="topbar-icon" />
              ) : (
                t('logout', '登出')
              )}
            </button>
          ) : (
            <button
              className={`login-button ${activeButton === 'login' ? 'btn-active' : ''}`}
              onClick={handleLoginClick}
            >
              {isMobile ? (
                <FaSignInAlt className="topbar-icon" />
              ) : currentLang === 'zh' ? (
                '登入'
              ) : currentLang === 'ja' ? (
                'ログイン'
              ) : (
                'Login'
              )}
            </button>
          )}
        </div>
      )}

      {/* 登出後提示框，使用 i18n 翻譯 */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={closeLogoutModal}
        title={t('logoutTitle', '提示')}
        message={t('logoutSuccess', '你已經登出了。')}
      />
    </div>
  );
}

export default TopBar;
